import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { usePortalAccess } from '../context/PortalAccessContext';
import axios from 'axios';
import { FaArrowLeft, FaMobileAlt, FaDatabase, FaSpinner } from 'react-icons/fa';

export default function YardDeviceDetailsPage() {
  const { yardNumber } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { access } = usePortalAccess();
  const portalEmail = (searchParams.get('portal_email') || access?.email || '').trim();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [timeRange, setTimeRange] = useState('7 days');
  const API_BASE = process.env.REACT_APP_API_SERVER_URL;

  const fetchYardData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE}/maiApi/analytics/yard/${yardNumber}/devices`, {
        params: {
          timeRange: timeRange,
          limit: 5000,
          ...(portalEmail ? { portal_email: portalEmail } : {})
        }
      });
      setData(response.data);
    } catch (err) {
      console.error('Error fetching yard data:', err);
      setError(err.response?.data?.error || 'Failed to fetch yard data');
    } finally {
      setLoading(false);
    }
  }, [API_BASE, timeRange, yardNumber, portalEmail]);

  useEffect(() => {
    fetchYardData();
  }, [fetchYardData]);

  const getFriendlyName = (deviceName) => {
    if (!deviceName) return 'Unknown Device';
    const parts = deviceName.split('_');
    if (parts.length >= 2) {
      return `${parts[0]} ${parts[1]}`;
    }
    return deviceName;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch (e) {
      return dateString;
    }
  };

  const getDeviceTelemetry = (androidId) => {
    if (!data || !data.telemetry_data) return [];
    return data.telemetry_data.filter(t => t.android_id === androidId);
  };

  if (loading) {
    return (
      <div className="w-full px-4 py-6">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <FaSpinner className="animate-spin text-4xl text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading yard device data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full px-4 py-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-red-800 mb-2">Error</h2>
          <p className="text-red-600">{error}</p>
          <button
            onClick={() => navigate('/comparative-analytics')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <FaArrowLeft />
            Back to Comparative Analytics
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const uniqueDevices = data.devices || [];
  const allTelemetry = data.telemetry_data || [];

  return (
    <div className="w-full px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <div className="mb-4 flex items-center gap-2">
          <button
            onClick={() => navigate('/comparative-analytics')}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
          >
            <FaArrowLeft />
            Back to Comparative Analytics
          </button>
          <button
            onClick={() => navigate(`/yard/${yardNumber}/layout`)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            View yard layout
          </button>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Yard {yardNumber} - Device Details</h1>
            <p className="text-gray-600 mt-2">
              {uniqueDevices.length} unique device{uniqueDevices.length !== 1 ? 's' : ''} • {allTelemetry.length} telemetry record{allTelemetry.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700">Time Range:</label>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="24 hours">Last 24 Hours</option>
              <option value="7 days">Last 7 Days</option>
              <option value="30 days">Last 30 Days</option>
            </select>
            <button
              onClick={fetchYardData}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Devices List */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
          <FaMobileAlt />
          Devices in Yard {yardNumber}
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300">
            <thead className="bg-blue-600 text-white">
              <tr>
                <th className="p-3 border text-left">Device Friendly Name</th>
                <th className="p-3 border text-left">Device Model</th>
                <th className="p-3 border text-left">Android ID</th>
                <th className="p-3 border text-left">UUID</th>
                <th className="p-3 border text-left">Carrier</th>
                <th className="p-3 border text-left">Phone</th>
                <th className="p-3 border text-left">Last Seen</th>
                <th className="p-3 border text-left">Records</th>
                <th className="p-3 border text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {uniqueDevices.length === 0 ? (
                <tr>
                  <td colSpan="9" className="p-4 text-center text-gray-500">
                    No devices found in this yard for the selected time range.
                  </td>
                </tr>
              ) : (
                uniqueDevices.map((device, idx) => {
                  const deviceTelemetry = getDeviceTelemetry(device.android_id);
                  return (
                    <tr key={idx} className="border-t hover:bg-gray-50">
                      <td className="p-3 border font-semibold">
                        {device.device_friendly_name || device.android_id}
                      </td>
                      <td className="p-3 border text-sm">
                        {getFriendlyName(device.device_name)}
                      </td>
                      <td className="p-3 border text-xs font-mono break-all">
                        {device.android_id}
                      </td>
                      <td className="p-3 border text-xs font-mono break-all">
                        {device.asset_tag || 'N/A'}
                      </td>
                      <td className="p-3 border">
                        {device.carrier || 'N/A'}
                      </td>
                      <td className="p-3 border text-sm">
                        {device.phone || 'N/A'}
                      </td>
                      <td className="p-3 border text-sm">
                        {formatDate(device.last_seen)}
                      </td>
                      <td className="p-3 border text-center">
                        <span className="font-semibold">{deviceTelemetry.length}</span>
                      </td>
                      <td className="p-3 border">
                        <button
                          onClick={() => setSelectedDevice(selectedDevice === device.android_id ? null : device.android_id)}
                          className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                        >
                          {selectedDevice === device.android_id ? 'Hide Data' : 'View Data'}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Selected Device Telemetry Data */}
      {selectedDevice && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-semibold flex items-center gap-2">
              <FaDatabase />
              Raw Telemetry Data - {uniqueDevices.find(d => d.android_id === selectedDevice)?.device_friendly_name || selectedDevice}
            </h2>
            <button
              onClick={() => setSelectedDevice(null)}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Close
            </button>
          </div>
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full border-collapse border border-gray-300 text-sm">
              <thead className="bg-gray-200 sticky top-0">
                <tr>
                  <th className="p-2 border text-left">Timestamp</th>
                  <th className="p-2 border text-left">Download (Mbps)</th>
                  <th className="p-2 border text-left">Upload (Mbps)</th>
                  <th className="p-2 border text-left">Ping (ms)</th>
                  <th className="p-2 border text-left">Signal (dBm)</th>
                  <th className="p-2 border text-left">Battery (%)</th>
                  <th className="p-2 border text-left">Carrier</th>
                </tr>
              </thead>
              <tbody>
                {getDeviceTelemetry(selectedDevice).length === 0 ? (
                  <tr>
                    <td colSpan="7" className="p-4 text-center text-gray-500">
                      No telemetry data found for this device.
                    </td>
                  </tr>
                ) : (
                  getDeviceTelemetry(selectedDevice).map((record, idx) => (
                    <tr key={idx} className="border-t hover:bg-gray-50">
                      <td className="p-2 border text-xs">
                        {formatDate(record.timestamp)}
                      </td>
                      <td className="p-2 border">
                        {record.download_mbps !== null && record.download_mbps !== undefined 
                          ? parseFloat(record.download_mbps).toFixed(2) 
                          : 'N/A'}
                      </td>
                      <td className="p-2 border">
                        {record.upload_mbps !== null && record.upload_mbps !== undefined 
                          ? parseFloat(record.upload_mbps).toFixed(2) 
                          : 'N/A'}
                      </td>
                      <td className="p-2 border">
                        {record.ping_ms !== null && record.ping_ms !== undefined 
                          ? parseFloat(record.ping_ms).toFixed(2) 
                          : 'N/A'}
                      </td>
                      <td className="p-2 border">
                        {record.lte_rsrp_dbm !== null && record.lte_rsrp_dbm !== undefined 
                          ? parseFloat(record.lte_rsrp_dbm).toFixed(2) 
                          : 'N/A'}
                      </td>
                      <td className="p-2 border">
                        {record.battery_percent !== null && record.battery_percent !== undefined 
                          ? parseFloat(record.battery_percent).toFixed(1) 
                          : 'N/A'}
                      </td>
                      <td className="p-2 border text-sm">
                        {record.carrier || 'N/A'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
